import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePrompt } from '@/components/DialogProvider';
import { useToast } from '@/components/ToastProvider';
import { errorMessage } from '@/lib/errors';
import {
  financeRefundsQuery,
  settleStableOperation,
  useInvalidateFinance,
  useRefundSplit,
  useRefundWriteOff,
  useStableOperationIds,
} from '@/lib/finance';
import { formatDateTime, formatMoney } from '@/lib/format';
import { toMinor } from '@/lib/money';
import { FINANCE_ATTEMPT_STATUS_TONE, FINANCE_REFUND_STATUS_TONE } from '@/lib/status-tone';
import type { FinanceRefund } from '@/api/generated/types.gen';

export interface RefundsCardProps {
  bookingId: string;
  /** finance.confirm — gates Split/Write off (I12); read-only otherwise. */
  canConfirm: boolean;
}

const STATUS_HELP: Record<FinanceRefund['status'], string> = {
  open: 'The provider has not confirmed this refund. Review provider activity before taking further action.',
  executed: 'The provider confirmed the refund. Account reconciliation is tracked separately in Payments.',
  written_off: 'The remaining obligation was closed without sending a refund.',
  superseded: 'This obligation was replaced. Review the replacement refunds on this booking.',
};

/**
 * The booking card's "Refunds" section (fix round 1, item 1 — Ruling
 * T23-1): every refund obligation on this booking (amount, status,
 * attempts, is_target) from GET /finance/refunds?booking_id=. Renders
 * nothing when the booking has no refund obligations at all — the common
 * case — rather than an empty card cluttering the payments tab.
 */
export function RefundsCard({ bookingId, canConfirm }: RefundsCardProps) {
  const { data, isPending, isError, error } = useQuery(financeRefundsQuery({ bookingId }));
  const askPrompt = usePrompt();
  const { toast } = useToast();
  // D-I4 (final review): pass bookingId so a Split/Write off here also
  // invalidates THIS page's Payments card (['booking', id,
  // 'payment-schedule'], a different query from the finance root) —
  // without it, the refund tree's server-side rewrite of the schedule
  // projections left the Payments card showing the old refund row next to
  // a Refunds card that already says "Written off".
  const refundSplit = useRefundSplit(bookingId);
  const refundWriteOff = useRefundWriteOff(bookingId);
  // Fix round 1, item 3 (T23-3): a stable operation_id per obligation,
  // reused across a failed-then-retried Split/Write off of the SAME row.
  const opIds = useStableOperationIds();
  // Fix round 2 (Critical): clear via settleStableOperation's mutateAsync
  // path, not a per-call mutate(vars, {onSuccess}) — two obligations' Split/
  // Write off can overlap, and the shared hook below only tracks one
  // mutation at a time (see settleStableOperation's own doc comment).
  const invalidateFinance = useInvalidateFinance();
  // The operation_conflict path inside settleStableOperation calls its 4th
  // arg with no args — bind bookingId here too, same reasoning as above.
  const invalidate = () => invalidateFinance(bookingId);

  const items = data?.items ?? [];
  if (!isPending && !isError && items.length === 0) return null;

  async function onSplit(refund: FinanceRefund) {
    const txt = await askPrompt({
      title: 'Split into parts',
      label: `Parts in ${refund.currency}, comma-separated (must sum to the obligation's amount)`,
      placeholder: '47.00, 70.00',
    });
    if (!txt) return;
    const tokens = txt.split(',').map((s) => s.trim());
    const parts: number[] = [];
    for (const [i, tok] of tokens.entries()) {
      const minor = toMinor(tok, refund.currency);
      if (minor === null || minor <= 0) {
        toast({ kind: 'error', title: `Part ${i + 1} ("${tok}") is not a valid amount` });
        return;
      }
      parts.push(minor);
    }
    if (parts.length < 2) {
      toast({ kind: 'error', title: 'Enter at least two amounts, comma-separated' });
      return;
    }
    const key = `refund-split:${refund.id}`;
    const result = await settleStableOperation(
      refundSplit.mutateAsync({ refundId: refund.id, partsMinor: parts, operationId: opIds.get(key) }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not split', message: result.message });
  }

  async function onWriteOff(refund: FinanceRefund) {
    const reason = await askPrompt({ title: 'Write off the rest?', label: 'Reason' });
    if (!reason?.trim()) return;
    const key = `refund-writeoff:${refund.id}`;
    const result = await settleStableOperation(
      refundWriteOff.mutateAsync({ refundId: refund.id, reason: reason.trim(), operationId: opIds.get(key) }),
      key,
      opIds,
      invalidate,
    );
    if (!result.ok) toast({ kind: 'error', title: 'Could not write off', message: result.message });
  }

  return (
    <section className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <h2 className="text-base font-semibold text-[var(--text-heading)]">Refunds</h2>
      <p className="mb-4 mt-1 text-sm text-muted">Track what is owed back and what the provider has confirmed.</p>
      {isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(error)}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map(({ refund, attempts }) => {
            const tone = FINANCE_REFUND_STATUS_TONE[refund.status];
            return (
              <li key={refund.id} className="rounded-[var(--radius-control)] border border-[var(--border-hairline)] p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={tone.variant} size="sm">
                    {tone.label}
                  </Badge>
                  <span className="tabular-nums font-semibold">{formatMoney(refund.amount_minor, refund.currency)}</span>
                  {refund.is_target && (
                    <Badge variant="warning" size="sm">
                      Current obligation
                    </Badge>
                  )}
                </div>
                <p className="mt-2 text-sm text-muted">{STATUS_HELP[refund.status]}</p>
                {refund.reason && <p className="mt-1 text-xs text-muted">{refund.reason}</p>}
                {attempts.length > 0 && (
                  <details className="mt-3" open={attempts.some((attempt) => attempt.status === 'unknown' || attempt.status === 'failed')}>
                    <summary className="cursor-pointer text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Provider attempts ({attempts.length})</summary>
                    <ul className="mt-2 flex flex-col gap-1">
                      {attempts.map((a) => {
                        const attemptTone = FINANCE_ATTEMPT_STATUS_TONE[a.status];
                        return (
                          <li key={a.id} className="flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-2 text-xs text-muted">
                            <Badge variant={attemptTone.variant} size="sm">
                              {attemptTone.label}
                            </Badge>
                            <span>{formatDateTime(a.first_sent_at ?? a.created_at)}</span>
                            {a.provider_ref && <span className="break-all">ref {a.provider_ref}</span>}
                            {a.error && <span className="basis-full text-destructive">{a.error}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                )}
                {canConfirm && refund.status === 'open' && (
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => void onSplit(refund)}>
                      Split
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => void onWriteOff(refund)}>
                      Write off
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
